// utils/feedUrlUtils.js - UPDATED with committee API support

/**
 * Generate feed URL from API endpoint with proper parameter extraction
 * Supports both standard feeds and committee API feeds
 * 
 * @param {string} apiEndpoint - The API endpoint string
 * @returns {Object} - Object containing path and search params
 */
 export const generateFeedUrlFromApiEndpoint = (apiEndpoint) => {
  try {
    // Clean the endpoint - remove leading & or ? if present
    let cleanEndpoint = apiEndpoint;
    if (cleanEndpoint.startsWith('&') || cleanEndpoint.startsWith('?')) {
      cleanEndpoint = cleanEndpoint.substring(1);
    }

    // Parse URL parameters
    const urlParams = new URLSearchParams(cleanEndpoint);
    
    // Check if this is a committee API endpoint
    const committee = urlParams.get('committee');
    const chamber = urlParams.get('chamber');
    const useCommitteeAPI = urlParams.get('useCommitteeAPI') === 'true' || !!committee;
    
    // Handle committee API endpoints
    if (useCommitteeAPI && committee) {
      const searchParams = new URLSearchParams();
      searchParams.set('useCommitteeAPI', 'true');
      searchParams.set('committee', committee);
      if (chamber) {
        searchParams.set('chamber', chamber);
      }
      
      const result = {
        path: '/feed/united_states_of_america/committee_materials',
        search: searchParams.toString(),
        fullUrl: `/feed/united_states_of_america/committee_materials?${searchParams.toString()}`,
        isCommitteeAPI: true
      };
      
      // console.log('Generated committee API URL:', result);
      return result;
    }
    
    // Standard feed handling
    const schema = urlParams.get('schema') || 'united_states_of_america';
    const table = urlParams.get('table') || urlParams.get('table_name');
    const orderBy = urlParams.get('order_by') || 'created_at';
    const filterColumn = urlParams.get('filter_column');
    const filterString = urlParams.get('filter_string');
    
    // console.log('generateFeedUrlFromApiEndpoint - Extracted params:', {
    //   schema,
    //   table,
    //   orderBy,
    //   filterColumn,
    //   filterString,
    //   originalEndpoint: apiEndpoint
    // });

    if (!table) {
      console.error('No table found in API endpoint:', apiEndpoint);
      return null;
    }

    // Build the feed path
    const feedPath = `/feed/${schema}/${table}`;
    
    // Build search parameters
    const searchParams = new URLSearchParams();
    searchParams.append('orderBy', orderBy);
    
    if (filterColumn && filterString) {
      searchParams.append('filterColumn', filterColumn);
      searchParams.append('filterString', filterString);
    }

    const result = {
      path: feedPath,
      search: searchParams.toString(),
      fullUrl: `${feedPath}${searchParams.toString() ? '?' + searchParams.toString() : ''}`,
      isCommitteeAPI: false
    };

    // console.log('Generated feed URL:', result);
    return result;
    
  } catch (error) {
    console.error('Error generating feed URL from endpoint:', apiEndpoint, error);
    return null;
  }
};

/**
 * Enhanced URL generation - returns just the full URL string
 * @param {string} apiParams - The API parameters string
 * @returns {string} - Clean feed URL
 */
export const generateFeedUrl = (apiParams) => {
  const urlData = generateFeedUrlFromApiEndpoint(apiParams);
  return urlData ? urlData.fullUrl : '/feed/united_states_of_america/default';
};

/**
 * Generate feed URL for department feeds specifically
 * @param {string} apiEndpoint - The API endpoint string
 * @param {string} agencyRoute - Optional agency route for department pages
 * @param {boolean} isDepartmentPage - Whether this is from a department page
 * @returns {Object} - Object containing navigation info
 */
export const generateDepartmentFeedUrl = (apiEndpoint, agencyRoute = null, isDepartmentPage = false) => {
  // If this is a department page and has an agency route, use that instead
  if (isDepartmentPage && agencyRoute) {
    // console.log('Department page - using agency route:', agencyRoute);
    return {
      path: agencyRoute,
      search: '',
      fullUrl: agencyRoute,
      isAgencyRoute: true,
      isCommitteeAPI: false
    };
  }

  // Otherwise, use the standard feed URL generation
  return generateFeedUrlFromApiEndpoint(apiEndpoint);
};

/**
 * Generate committee materials feed URL
 * @param {string} committeeName - The committee name
 * @param {string} chamber - The chamber (HOUSE or SENATE)
 * @returns {string} - The full feed URL
 */
export const generateCommitteeMaterialsUrl = (committeeName, chamber = 'HOUSE') => {
  const searchParams = new URLSearchParams();
  searchParams.set('useCommitteeAPI', 'true');
  searchParams.set('committee', committeeName);
  searchParams.set('chamber', chamber);
  
  return `/feed/united_states_of_america/committee_materials?${searchParams.toString()}`;
};

/**
 * Parse committee API parameters from URL
 * @param {string} urlSearch - The URL search string (location.search)
 * @returns {Object|null} - Committee parameters or null
 */
export const parseCommitteeParams = (urlSearch) => {
  try {
    const searchParams = new URLSearchParams(urlSearch);
    const useCommitteeAPI = searchParams.get('useCommitteeAPI') === 'true';
    const committee = searchParams.get('committee');
    const chamber = searchParams.get('chamber');
    
    if (useCommitteeAPI && committee) {
      return {
        useCommitteeAPI: true,
        committee: decodeURIComponent(committee),
        chamber: chamber ? decodeURIComponent(chamber) : null
      };
    }
    
    return null;
  } catch (error) {
    console.error('Error parsing committee params:', error);
    return null;
  }
};

/**
 * Check if an API endpoint is a committee API endpoint
 * @param {string} apiEndpoint - The API endpoint string
 * @returns {boolean}
 */
export const isCommitteeApiEndpoint = (apiEndpoint) => {
  if (!apiEndpoint) return false;
  
  try {
    let cleanEndpoint = apiEndpoint;
    if (cleanEndpoint.startsWith('&') || cleanEndpoint.startsWith('?')) {
      cleanEndpoint = cleanEndpoint.substring(1);
    }
    
    const urlParams = new URLSearchParams(cleanEndpoint);
    return !!urlParams.get('committee') || urlParams.get('useCommitteeAPI') === 'true';
  } catch (error) {
    return false;
  }
};